import { useState } from "react";
import { supabase } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Edit, Trash, Plus, CheckCircle, XCircle } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";

type Category = {
  id: string;
  name: string;
  slug: string;
  created_at?: string;
  updated_at?: string;
};

type CategoryManagerProps = {
  categories: Category[];
  onCategoriesChange: () => void;
  isLoading: boolean;
};

export function CategoryManager({
  categories,
  onCategoriesChange,
  isLoading,
}: CategoryManagerProps) {
  const [currentCategory, setCurrentCategory] = useState<Partial<Category>>({});
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [formErrors, setFormErrors] = useState<{
    name?: string;
    slug?: string;
  }>({});
  const [savingCategory, setSavingCategory] = useState(false);
  const [deletingCategory, setDeletingCategory] = useState(false);
  const { toast } = useToast();

  const handleEdit = (category: Category) => {
    setCurrentCategory({ ...category });
    setIsEditDialogOpen(true);
  };

  const handleCreate = () => {
    setCurrentCategory({
      name: "",
      slug: "",
    });
    setIsEditDialogOpen(true);
  };

  const handleDelete = (category: Category) => {
    setCurrentCategory(category);
    setIsDeleteDialogOpen(true);
  };

  const validateForm = () => {
    const errors: { name?: string; slug?: string } = {};
    if (!currentCategory.name) errors.name = "Tên danh mục không được để trống";
    if (!currentCategory.slug) errors.slug = "Slug không được để trống";

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateSlug = (name: string) => {
    return name
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/đ/g, "d")
      .replace(/[^a-z0-9\s]/g, "")
      .replace(/\s+/g, "-");
  };

  const handleNameChange = (name: string) => {
    setCurrentCategory({
      ...currentCategory,
      name,
      slug: generateSlug(name),
    });
  };

  const handleSaveCategory = async () => {
    if (!validateForm()) return;

    const isNew = !currentCategory.id;
    const now = new Date().toISOString();
    setSavingCategory(true);

    try {
      if (isNew) {
        const { error } = await supabase.from("categories").insert({
          name: currentCategory.name,
          slug: currentCategory.slug,
          created_at: now,
          updated_at: now,
        });

        if (error) throw error;
        toast({
          title: "Thành công",
          description: "Đã thêm danh mục mới",
          variant: "default",
        });
      } else {
        const { error } = await supabase
          .from("categories")
          .update({
            name: currentCategory.name,
            slug: currentCategory.slug,
            updated_at: now,
          })
          .eq("id", currentCategory.id!);

        if (error) throw error;
        toast({
          title: "Thành công",
          description: "Đã cập nhật danh mục",
          variant: "default",
        });
      }

      onCategoriesChange();
      setIsEditDialogOpen(false);
    } catch (error: Error | unknown) {
      console.error("Error saving category:", error);
      toast({
        title: "Lỗi",
        description: `Không thể ${isNew ? "thêm" : "cập nhật"} danh mục: ${
          error instanceof Error ? error.message : "Lỗi không xác định"
        }`,
        variant: "destructive",
      });
    } finally {
      setSavingCategory(false);
    }
  };

  const handleConfirmDelete = async () => {
    try {
      if (!currentCategory.id) return;
      setDeletingCategory(true);

      // Kiểm tra xem danh mục có danh mục con hay không
      const { data: subcategories, error: countError } = await supabase
        .from("subcategories")
        .select("id")
        .eq("category_id", currentCategory.id);

      if (countError) throw countError;

      if (subcategories && subcategories.length > 0) {
        toast({
          title: "Không thể xóa",
          description:
            "Danh mục này có chứa danh mục con. Vui lòng xóa các danh mục con trước.",
          variant: "destructive",
        });
        setIsDeleteDialogOpen(false);
        return;
      }

      // Kiểm tra xem danh mục có bài viết hay không
      const { data: articles, error: articlesError } = await supabase
        .from("articles")
        .select("id")
        .eq("category_id", currentCategory.id);

      if (articlesError) throw articlesError;

      if (articles && articles.length > 0) {
        toast({
          title: "Không thể xóa",
          description:
            "Danh mục này có chứa bài viết. Vui lòng xóa các bài viết trước.",
          variant: "destructive",
        });
        setIsDeleteDialogOpen(false);
        return;
      }

      // Tiến hành xóa danh mục
      const { error } = await supabase
        .from("categories")
        .delete()
        .eq("id", currentCategory.id);

      if (error) throw error;

      toast({
        title: "Thành công",
        description: "Đã xóa danh mục",
        variant: "default",
      });
      onCategoriesChange();
      setIsDeleteDialogOpen(false);
    } catch (error: Error | unknown) {
      console.error("Error deleting category:", error);
      toast({
        title: "Lỗi",
        description: `Không thể xóa danh mục: ${
          error instanceof Error ? error.message : "Lỗi không xác định"
        }`,
        variant: "destructive",
      });
    } finally {
      setDeletingCategory(false);
    }
  };

  return (
    <div>
      <div className="flex justify-end mb-4">
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Thêm danh mục
        </Button>
      </div>

      <div className="overflow-x-auto -mx-4 md:mx-0">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="font-medium">Tên danh mục</TableHead>
              <TableHead className="hidden md:table-cell">Slug</TableHead>
              <TableHead className="text-right">Hành động</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center">
                  Đang tải...
                </TableCell>
              </TableRow>
            ) : categories.length === 0 ? (
              <TableRow>
                <TableCell colSpan={3} className="text-center">
                  Không có danh mục nào
                </TableCell>
              </TableRow>
            ) : (
              categories.map((category) => (
                <TableRow key={category.id}>
                  <TableCell className="font-medium">{category.name}</TableCell>
                  <TableCell className="hidden md:table-cell truncate max-w-[150px]">
                    {category.slug}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(category)}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(category)}
                      >
                        <Trash className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-md max-h-[90vh] w-[95vw]">
          <DialogHeader>
            <DialogTitle>
              {currentCategory.id ? "Chỉnh sửa danh mục" : "Thêm danh mục mới"}
            </DialogTitle>
            <DialogDescription>
              Điền đầy đủ thông tin để {currentCategory.id ? "cập nhật" : "tạo"}{" "}
              danh mục
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid gap-4">
              <div className="space-y-2">
                <Label htmlFor="name">Tên danh mục</Label>
                <Input
                  id="name"
                  value={currentCategory.name || ""}
                  onChange={(e) => handleNameChange(e.target.value)}
                  className={formErrors.name ? "border-red-500" : ""}
                />
                {formErrors.name && (
                  <p className="text-sm text-red-500">{formErrors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="slug">Slug</Label>
                <Input
                  id="slug"
                  value={currentCategory.slug || ""}
                  onChange={(e) =>
                    setCurrentCategory({
                      ...currentCategory,
                      slug: e.target.value,
                    })
                  }
                  className={formErrors.slug ? "border-red-500" : ""}
                />
                {formErrors.slug && (
                  <p className="text-sm text-red-500">{formErrors.slug}</p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button
              variant="outline"
              onClick={() => setIsEditDialogOpen(false)}
              disabled={savingCategory}
              className="w-full sm:w-auto"
            >
              Hủy
            </Button>
            <Button
              onClick={handleSaveCategory}
              disabled={savingCategory}
              className="flex items-center w-full sm:w-auto"
            >
              {savingCategory ? (
                <>Đang lưu...</>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {currentCategory.id ? "Cập nhật" : "Tạo mới"}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent className="w-[95vw] max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Xác nhận xóa danh mục</AlertDialogTitle>
            <AlertDialogDescription>
              Bạn có chắc chắn muốn xóa danh mục "{currentCategory.name}" không?
              Hành động này không thể hoàn tác.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel
              disabled={deletingCategory}
              className="w-full sm:w-auto"
            >
              Hủy
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deletingCategory}
              className="flex items-center bg-red-500 hover:bg-red-600 w-full sm:w-auto"
            >
              {deletingCategory ? (
                <>Đang xóa...</>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Xóa
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
